export const uploadStatus = {
  FAIL: 'fail', //上传失败
  SUCCESS: 'success', //上传成功
  UPLOADING: 'uploading', //上传中
  PENDING: "pending" //等待上传
}

export class EnhancedUploadManager {
  /**
   * 构造函数
   * @param {Object} options 配置选项
   * @param {string} options.userNo 用户唯一标识
   * @param {Function} options.requestAdapter 外部请求
   * @param {Object} [options.storage=localStorage] 存储接口
   * @param {string} [options.storageKey='upload_manager_data'] 存储键名
   * @param {number} [options.maxRetries=3] 最大重试次数
   * @param {number} [options.maxConcurrent=3] 最大并发数
   */
  constructor(options) {
    this.requestAdapter = options.requestAdapter || function () { };
    this.storage = options.storage || window.localStorage;
    this.storageKey = options.storageKey || 'upload_manager_data';
    this.maxConcurrent = options.maxConcurrent || 3;
    this.maxRetries = options.maxRetries || 3;
    this.activeUploads = new Map();
    this.pendingQueue = [];
    this.currentConcurrent = 0;
    this.stopUplad = false

    this._initStorage();
  }

  /**
   * 初始化存储结构
   * @private
   */
  _initStorage() {
    const data = this._getStorageData();
    if (!data.users) {
      this._saveStorageData({
        users: {},
        metadata: {
          createdAt: Date.now(),
          lastUpdated: Date.now()
        }
      });
    }
  }

  /**
   * 获取存储数据
   * @private
   * @return {Object}
   */
  _getStorageData() {
    const dataStr = this.storage?.getItem(this.storageKey);
    return dataStr ? JSON.parse(dataStr) : {};
  }

  /**
   * 保存存储数据
   * @private
   * @param {Object} data 数据对象
   */
  _saveStorageData(data) {
    data.metadata = data.metadata || {};
    data.metadata.lastUpdated = Date.now();
    this.storage?.setItem(this.storageKey, JSON.stringify(data));
  }

  /**
   * 获取用户数据
   * @private
   * @param {String} userNo 用户id
   * @return {Object}
   */
  _getUserData(userNo) {
    const allData = this._getStorageData();
    if (!allData.users[userNo]) {
      allData.users[userNo] = {
        uploads: {},
        stats: {
          total: 0
        },
        createdAt: Date.now(),
        lastUpdated: Date.now()
      };
      this._saveStorageData(allData);
    }
    return allData.users[userNo];
  }

  /**
   * 获取所有待上传数据的用户
   * @private
   * @return {Object}
   */
  _getAllUser() {
    const allData = this._getStorageData();
    return Object.keys(allData.users);
  }

  /**
   * 更新用户数据
   * @private
   * @param {String} userNo 用户id
   * @param {Function} updater 更新函数
   */
  _updateUserData(userNo, updater) {
    const allData = this._getStorageData();
    const userData = this._getUserData(userNo);
    updater(userData);
    allData.users[userNo] = userData;
    this._saveStorageData(allData);
  }


  /**
  * 更新数据状态
  * @private
  * @param {String} userNo 用户id
  * @param {String} requestId 请求id
  * @param {Object} status 上传状态
  */
  updateUserDataStatus(userNo, requestId, status) {
    this._updateUserData(userNo, (userData) => {
      if (userData.uploads[requestId]) {
        userData.uploads[requestId].status = status
        userData.uploads[requestId].updatedAt = Date.now()
      }
    })
  }


  /**
   * 生成请求ID
   * @private
   * @return {string}
   */
  _generateRequestId() {
    return `req_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
  }


  /**
   * 记录新请求
  * @param {Object} params 参数对象
  * @param {string} params.userNo 用户id
  * @param {Object} params.requestConfig 请求配置对象
  * @param {*} params.data 要上传的数据
  * @return {string} 请求ID
  */
  registerUpload({ userNo, requestConfig, data }) {
    const requestId = this._generateRequestId();

    this._updateUserData(userNo, userData => {
      userData.uploads[requestId] = {
        requestId,
        data: data,
        config: requestConfig,
        status: uploadStatus.UPLOADING,
        attempts: 0,
        createdAt: Date.now(),
        updatedAt: Date.now()
      };
      userData.stats.total += 1;
    });
    return requestId;
  }

  /**
   * 开始上传
   * @param {string} userNo 用户ID
   * @param {string} requestId 请求ID
   * @return {Promise}
   */
  async startUpload(userNo, requestId) {
    const userData = this._getUserData(userNo);
    const upload = userData.uploads[requestId];

    //没有这个上传文件
    if (!upload) {
      return console.log(`No upload found for ${requestId}`)
    }

    //该文件正在上传
    if (this.activeUploads.has(requestId)) {
      console.log(this.activeUploads)
      return console.log(`Upload ${requestId} already in progress`)
    }

    // 检查并发限制
    if (this.currentConcurrent >= this.maxConcurrent) {
      return new Promise(resolve => {
        this.pendingQueue.push(() => {
          resolve(this._executeUpload(userNo, requestId));
        });
      });
    }

    return this._executeUpload(userNo, requestId);
  }

  /**
   * 执行上传
   * @private
   * @param {string} userNo 用户id
   * @param {string} requestId 请求ID
   * @return {Promise}
   */
  async _executeUpload(userNo, requestId) {
    this.currentConcurrent += 1;

    try {
      const userData = this._getUserData(userNo);
      const upload = userData.uploads[requestId];

      //没有上传文件｜｜文件已上传成功
      if (!upload || upload.status === uploadStatus.SUCCESS) {
        return console.log(`Invalid upload state for ${requestId}`)
      }

      //添加当前正在上传
      this.activeUploads.set(requestId, {})

      // 更新数据状态
      this._updateUserData(userNo, userData => {
        if (userData.uploads[requestId]) {
          userData.uploads[requestId].status = uploadStatus.UPLOADING;
          userData.uploads[requestId].attempts += 1;
          userData.uploads[requestId].updatedAt = Date.now();
        }
      });

      //发起请求
      this.requestAdapter({
        ...upload.config,
        requestId,
        data: upload.data,
        userNo
      });

    } catch (error) {
      console.log('_executeUpload', error)
      //处理请求错误
      await this.handleUploadError(userNo, requestId, error);
    } finally {
      //请求已完成（无论成功失败）
      this.currentConcurrent -= 1;
      this.activeUploads.delete(requestId);
      //执行下一个请求
      this._processQueue();
    }
  }

  /**
   * 上传错误重试
   * @private
   * @param {String} userNo 用户id
   * @param {string} requestId 请求ID
   * @param {Error} error 错误对象
   * @param {boolean} isNotRestart 是否不需要重试
   */
  async handleUploadError(userNo, requestId, error, isNotRestart = false) {
    const userData = this._getUserData(userNo);
    const upload = userData.uploads[requestId];

    if (!upload) {
      return console.log(`No failed upload found for ${requestId}`)
    }

    // 更新错误信息
    this._updateUserData(userNo, userData => {
      userData.uploads[requestId].lastError = this._normalizeError(error);
      userData.uploads[requestId].updatedAt = Date.now();
      userData.uploads[requestId].status = uploadStatus.FAIL;
    });

    //暂停上传
    if (this.stopUplad || isNotRestart) return

    //重试一下
    if (upload.attempts < this.maxRetries) {
      this.startUpload(userNo, requestId);
    }
  }

  /**
   * 处理队列中的请求
   * @private
   */
  _processQueue() {
    while (this.pendingQueue.length > 0 && this.currentConcurrent < this.maxConcurrent) {
      const next = this.pendingQueue.shift();
      next();
    }
  }

  /**
   * 标准化错误对象
   * @private
   * @param {Error} error
   * @return {Object}
   */
  _normalizeError(error) {
    return {
      msg: error?.msg,
      data: error?.data,
      message: error.message,
      stack: error.stack,
      ...(error.response && { response: error.response }),
      ...(error.status && { status: error.status }),
      ...(error.code && { code: error.code })
    };
  }

  /**
   * 获取用户所有上传记录
   * @param {string} [userNo] 用户id
   * @param {string} [status] 过滤状态
   * @return {Array}
   */
  getUploads(userNo, status) {
    const userData = this._getUserData(userNo);
    const uploads = Object.values(userData.uploads);
    return status ? uploads.filter(u => u.status === status) : uploads;
  }

  /**
   * 获取统计信息
   * @param {string} [userNo] 用户id
   * @return {Object}
   */
  getStats(userNo) {
    return this._getUserData(userNo).stats;
  }

  /**
   * 上传
   * @param {string} [userNo] 用户id
   * @param {string} requestId 请求ID
   * @return {Promise}
   */
  async retryUpload(userNo, requestId) {
    console.log('retryUpload', userNo, requestId)

    const upload = this._getUserData(userNo).uploads[requestId];

    //没有该数据
    if (!upload) {
      return console.log(`No failed upload found for ${requestId}`)
    }

    // 重置状态
    this._updateUserData(userNo, userData => {
      userData.uploads[requestId].status = uploadStatus.PENDING;
      userData.uploads[requestId].attempts = 0;
    });

    this.startUpload(userNo, requestId).catch(error => {
      console.log('startUpload', error)
      this.handleUploadError(userNo, requestId, error)
    });

  }

  /**
   * 用户所有的上传 || 不传userNo默认上传所有用户文件
   * @param {string} [userNo] 用户id
   */
  async retryAllFailedUpload(userNo) {
    let failedUploads = []

    if (userNo) {
      //获取单个用户上传数据(转Array)
      const uploads = this.getUploads(userNo);
      //组合数据
      failedUploads = uploads.length ? [{ userNo, uploads }] : [];
    } else {
      //获取所有用户
      const allUsers = this._getAllUser();
      //获取所有用户数据(转Array)
      failedUploads = allUsers.map(userNo => ({ userNo, uploads: this.getUploads(userNo) }))
    }

    console.log('retryAllFailedUpload', userNo)
    console.log('retryAllFailedUpload', JSON.stringify(failedUploads))

    //没有要上传的数据
    if (!failedUploads.length) return
    //当前正在执行上传并且队列不为空 (防止多次重复上传)
    if (!this.startUpload && this.pendingQueue.length) return

    this.stopUplad = false

    for (const upload of failedUploads) {
      const { userNo, uploads } = upload
      for (const upload of uploads) {
        this.retryUpload(userNo, upload.requestId);
      }
    }
  }

  /**
   * 暂停所有上传
   */
  async stopAllUpload() {
    console.log('停止所有上传')
    this.pendingQueue = []
    this.activeUploads.clear()
    this.stopUplad = true
  }

  /**
   * 清除上传记录
   * @param {string} userNo 用户id
   * @param {string} [requestId] 请求ID（不传则清除所有）
   */
  clearUploads(userNo, requestId) {
    this._updateUserData(userNo, userData => {
      if (requestId) {
        userData.stats.total -= 1
        delete userData.uploads[requestId];
      } else {
        userData.uploads = {};
        userData.stats.total = 0
      }
    });
  }
}


export class SealUploadManager extends EnhancedUploadManager {

  userInfo = {}

  constructor(options) {
    super(options)
  }

  /**
  * 函数代理
  * @param {string} name 方法名称
  * @param {string} fn 函数
  */
  registerProxyFunction(fnName, fn) {

    const proxFun = async ({ userNo, requestId, notCacheFile = false, isNotRestart = true } = {}, ...args) => {
      return new Promise(async (resolve, reject) => {
        console.log('options', userNo, requestId, notCacheFile , isNotRestart, args)
        //没有userNo默认使用当前登录用户
        userNo = userNo || this.userInfo.userNo || Date.now()
        if (!requestId && !notCacheFile) {
          requestId = this.registerUpload({ userNo, requestConfig: { fnName }, data: args })
        }
        try {
          const result = await fn.apply(this, args)
          console.log('result请求结果', result)
          if (result.success) {
            !notCacheFile && this.clearUploads(userNo, requestId)
          } else {
            !notCacheFile && this.handleUploadError(userNo, requestId, { message: result.message, code: result.code }, isNotRestart)
          }
          //请求已完成（无论成功失败）
          this.currentConcurrent -= 1;
          this.activeUploads.delete(requestId);
          resolve(result)
        } catch (error) {
          console.log('proxFun', error)
          //请求已完成（无论成功失败）
          this.currentConcurrent -= 1;
          this.activeUploads.delete(requestId);
          !notCacheFile && this.handleUploadError(userNo, requestId, error, isNotRestart)
          resolve({
            success: false,
            error: this._normalizeError(error)
          })
        } finally {
          if (!isNotRestart) {
            //执行下一个请求
            this._processQueue();
          }
        }
      })

    }

    this[fnName] = proxFun.bind(this)
  }

  /**
   * 执行上传
   * @private
   * @param {string} userNo 用户id
   * @param {string} requestId 请求ID
   * @return {Promise}
   */
  async _executeUpload(userNo, requestId) {
    console.log('走了这个上传', userNo, requestId)
    this.currentConcurrent += 1;
    const userData = this._getUserData(userNo);
    const upload = userData.uploads[requestId];

    //没有上传文件｜｜文件已上传成功
    if (!upload || upload.status === uploadStatus.SUCCESS) {
      return console.log(`Invalid upload state for ${requestId}`)
    }

    //添加当前正在上传
    this.activeUploads.set(requestId, {})

    // 更新数据状态
    this._updateUserData(userNo, userData => {
      if (userData.uploads[requestId]) {
        userData.uploads[requestId].status = uploadStatus.UPLOADING;
        userData.uploads[requestId].attempts += 1;
        userData.uploads[requestId].updatedAt = Date.now();
      }
    });

    //发起请求
    this[upload.config['fnName']] && this[upload.config['fnName']].apply(this, [{ userNo, requestId, isNotRestart: false }, ...upload.data]);
  }
}
